import React, { useCallback, useContext, useEffect, useState } from 'react';

import { Skeleton } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as AlertIcon } from '../../../../../assets/icons/v3/alert-circle.svg';
import { ReactComponent as ErrorIcon } from '../../../../../assets/icons/v3/error.svg';
import { ReactComponent as InfoIcon } from '../../../../../assets/icons/v3/information-icon.svg';
import BdsButton from '../../../../../shared/components/v3/Buttons/BdsButton/BdsButton';
import BdsCardContainer from '../../../../../shared/components/v3/Cards/BdsCardContainer/BdsCardContainer';
import Toast from '../../../../../shared/components/v3/Toast/Toast';
import { BALANCE_CHARGE_ROUTE } from '../../../../../shared/constants/auth';
import { SEVENTY_TWO_HOURS_IN_MS } from '../../../../../shared/constants/constants';
import { TOAST_ALERT_BALANCE_TIMESTAMP } from '../../../../../shared/constants/localStorage';
import { CLICK_ACTION, RECHARGE_BALANCE_RF } from '../../../../../shared/constants/tagManager';
import { CompanyContext } from '../../../../../shared/context/company/companyContext';
import { IFlexibleRemunerationWallet } from '../../../../../shared/interfaces/company';
import Company from '../../../../../shared/services/api/companies/companies';
import TagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_GLOBAL } from '../../../../../shared/translations/commons';
import { TRANSLATIONS_HOME } from '../../../../../shared/translations/home';
import { toLocaleNumberFormat } from '../../../../../shared/utils/utils';

import './BalanceCard.scss';

const BalanceCard = () => {
  const [openToast, setOpenToast] = useState<boolean>(false);
  const [walletData, setWalletData] = useState<IFlexibleRemunerationWallet | undefined>();
  const [error, setError] = useState<boolean>(false);

  const { companyData, isLoadingCompanyData, company } = useContext(CompanyContext);

  const isValidCompany = useCallback(() => {
    return companyData && companyData.id !== '' && companyData.id === company.id;
  }, [companyData, company]);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const getCardClass = (walletData?: IFlexibleRemunerationWallet) => {
    if (!walletData) return '';
    if (walletData.balance <= 0) return 'negative';
    if (walletData.balance <= walletData.minThreshold) return 'alert';
    return '';
  };

  const cardClass = getCardClass(walletData);

  const handleClick = (hasTag: boolean) => {
    if (hasTag) {
      TagManagerService.pushEvent(CLICK_ACTION, RECHARGE_BALANCE_RF);
    }
    navigate(BALANCE_CHARGE_ROUTE);
  };

  const handleCloseToast = (_: React.SyntheticEvent | React.MouseEvent, reason?: string) => {
    if (reason !== 'clickaway') {
      setOpenToast(false);
    }
  };

  const handleStatus = (balance: number, minThreshold: number) => {
    if (balance <= 0) {
      return (
        <div className="bc-status-container">
          <ErrorIcon className="bc-icon" /> {t(TRANSLATIONS_HOME.balanceCard.balance.negative)}
        </div>
      );
    }
    if (balance <= minThreshold) {
      return (
        <div className="bc-status-container">
          <AlertIcon className="bc-icon" /> {t(TRANSLATIONS_HOME.balanceCard.balance.alert)}
        </div>
      );
    }
    return (
      <div className="bc-status-container">
        <InfoIcon className="bc-icon" /> {t(TRANSLATIONS_HOME.balanceCard.balance.info)}
      </div>
    );
  };

  const shouldShowToast = (): boolean => {
    const storedTimestamp = localStorage.getItem(TOAST_ALERT_BALANCE_TIMESTAMP);

    if (!storedTimestamp) {
      return true;
    }
    const now = new Date().getTime();
    const timeSinceLastHide = now - parseInt(storedTimestamp, 10);

    return timeSinceLastHide > SEVENTY_TWO_HOURS_IN_MS;
  };

  useEffect(() => {
    const isAlertOrNegative = cardClass === 'alert' || cardClass === 'negative';
    if (isAlertOrNegative && shouldShowToast()) {
      setOpenToast(true);
    }
  }, [cardClass]);

  const fetchData = useCallback(async () => {
    setError(false);
    setWalletData(undefined);
    try {
      const response = await Company.getCompanyFlexibleRemunerationWallet(companyData.id);
      setWalletData(response);
    } catch {
      setError(true);
    }
  }, [companyData.id]);

  useEffect(() => {
    if (isValidCompany() && !isLoadingCompanyData) {
      fetchData();
    }
  }, [fetchData, isValidCompany, isLoadingCompanyData]);

  if (error) {
    return (
      <BdsCardContainer borderRadiusSize="radius-l" paddingSize="padding-m" className="home-bc-container">
        <div className="bc-error-content">
          <div className="bc-error-title">{t(TRANSLATIONS_HOME.balanceCard.error.title)}</div>
          <div className="bc-error-message">{t(TRANSLATIONS_HOME.balanceCard.error.description)}</div>
        </div>
      </BdsCardContainer>
    );
  }

  if (!walletData) {
    return <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: '32px' }} />;
  }

  return (
    <>
      <BdsCardContainer
        borderRadiusSize="radius-l"
        paddingSize="padding-m"
        className={`home-bc-container ${cardClass}`}
      >
        <div className="bc-title-container">
          {t(TRANSLATIONS_HOME.balanceCard.main)}
          <BdsButton variant="primary" size="s" action={() => handleClick(true)}>
            {t(TRANSLATIONS_HOME.balanceCard.button)}
          </BdsButton>
        </div>
        <div className="bc-balance-container">
          {toLocaleNumberFormat(
            walletData.balance,
            { style: 'currency', currency: walletData.currency, useGrouping: true },
            'es-ES'
          )}
        </div>
        {handleStatus(walletData.balance, walletData.minThreshold)}
      </BdsCardContainer>

      <Toast
        open={openToast && cardClass === 'negative'}
        icon="error"
        title={t(TRANSLATIONS_GLOBAL.toasts.flexibleRemunerationNegativeBalance.title)}
        description={
          <Trans
            i18nKey={TRANSLATIONS_GLOBAL.toasts.flexibleRemunerationNegativeBalance.description}
            components={{
              u: (
                <button
                  className="home-bc-toast-link"
                  data-testid="toast-negative-description"
                  onClick={() => handleClick(false)}
                />
              )
            }}
          />
        }
        button={true}
        handleClose={handleCloseToast}
      />
      <Toast
        open={openToast && cardClass === 'alert'}
        icon="alert"
        title={t(TRANSLATIONS_GLOBAL.toasts.flexibleRemunerationAlertBalance.title)}
        description={
          <Trans
            i18nKey={TRANSLATIONS_GLOBAL.toasts.flexibleRemunerationAlertBalance.description}
            components={{
              u: (
                <button
                  className="home-bc-toast-link"
                  data-testid="toast-alert-description"
                  onClick={() => handleClick(false)}
                />
              )
            }}
          />
        }
        button={true}
        handleClose={handleCloseToast}
      />
    </>
  );
};

export default BalanceCard;
